import {
    AppBar,
    getUserSettingsModalSearch,
    getUserSettingsModalUrl,
    NavigationMenuItem,
    parseUserSettingsModalTab,
    removeUserSettingsModalSearch,
    tokens,
    TOOLBAR_HEIGHT,
    USER_SETTINGS_SEARCH_PARAM,
} from 'ComponentLibrary'
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { ReactNode, useEffect } from 'react'
import { compact, isEmpty } from 'lodash'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { useCustomerSelection } from '../context/CustomerSelection'
import { useIamContext } from 'ClientPortal/src/context/IAmContext'
import { useSignOut } from 'ClientPortal/src/hooks/useSignOut'
import { DYNAMIC_APP_PATHS, STATIC_APP_PATHS } from 'ClientPortal/src/router/constants'
import {
    BuildingOfficeIcon,
    ClipboardTextIcon,
    FilesIcon,
    GearIcon,
    PulseIcon,
    SquaresFourIcon,
    TrayIcon,
    UserCircleIcon,
} from '@phosphor-icons/react'
import { TaskSideDrawerWithContext } from 'ClientPortal/src/pages/SideDrawers/TaskSideDrawerWithContext'
import { Box } from '@mui/material'
import { NavigationMenuItemWithImage } from 'ComponentLibrary/src/molecules/AppBar/AppBar'
import type { RootUser as User } from '@einwert/valuation-api-client'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'
import { UserSettingsModal } from 'ClientPortal/src/pages/Settings/UserSettingsModalPage'

type LayoutProps = {
    children: ReactNode
}

export function Layout({ children }: LayoutProps) {
    const location = useLocation()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const { user, isUserPending } = useIamContext()
    const { signOut } = useSignOut()
    const { t: tTerminology } = useTranslation('terminology', { keyPrefix: 'common' })
    const { t } = useTranslation('pages', { keyPrefix: 'layout.navigation' })
    const currentLocale = useCurrentResolvedLocale()
    const settingsModalTab = parseUserSettingsModalTab(searchParams.get(USER_SETTINGS_SEARCH_PARAM))
    const profileSettingsUrl = getUserSettingsModalUrl({
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
        tab: 'profile',
    })

    const closeUserSettingsModal = () => {
        void navigate(
            {
                pathname: location.pathname,
                search: removeUserSettingsModalSearch(location.search),
                hash: location.hash,
            },
            { replace: true }
        )
    }

    const handleSettingsTabChange = (tab: NonNullable<typeof settingsModalTab>) => {
        void navigate(
            {
                pathname: location.pathname,
                search: getUserSettingsModalSearch(location.search, tab),
                hash: location.hash,
            },
            { replace: true }
        )
    }

    useEffect(() => {
        const path = location.pathname.split('/')[1]
        const titleNavigationItems = [
            ...getNavigationMenuItems(t, tTerminology),
            getInboxNavigationItem(t),
            getUserNavigationItems(user, t, STATIC_APP_PATHS.userDetailsPage),
        ]
        const page = titleNavigationItems.find((item) => item.path.includes(`/${path}`))
        document.title = compact([page?.label, 'einwert']).join(' - ')
    }, [currentLocale, location.pathname, t, tTerminology, user])

    const { customersForSelection, selectedCustomer, setSelectedCustomer, isCustomersInitialLoading } = useCustomerSelection()
    const navigationMenuItems = getNavigationMenuItems(t, tTerminology)
    const inboxNavigationItem = getInboxNavigationItem(t)
    const userNavigationItem = getUserNavigationItems(user, t, profileSettingsUrl)

    const customerSelectionOptions = !isEmpty(customersForSelection)
        ? {
              selectedCustomer: selectedCustomer?.uuid,
              customers: customersForSelection
                  ?.map((customer) => ({ label: customer.name, value: customer.uuid }))
                  .sort((a, b) => a.label.localeCompare(b.label)),
              setSelectedCustomer,
          }
        : undefined

    if (isUserPending || isCustomersInitialLoading) {
        return null
    }

    return (
        <Box>
            <AppBar
                navigationMenuItems={navigationMenuItems}
                currentPath={location.pathname}
                customerSelection={customerSelectionOptions}
                userMenuItem={userNavigationItem}
                inboxMenuItem={inboxNavigationItem}
                logout={signOut}
                logoutLabel={tTerminology('logout')}
            />
            <TaskSideDrawerWithContext />
            <Box
                sx={{
                    height: `calc(100vh - ${TOOLBAR_HEIGHT})`,
                    overflowY: 'auto',
                }}
            >
                {children}
            </Box>
            {settingsModalTab && (
                <UserSettingsModal selectedTab={settingsModalTab} onTabChange={handleSettingsTabChange} onClose={closeUserSettingsModal} />
            )}
        </Box>
    )
}

export function LayoutWrapper() {
    return (
        <Layout>
            <Outlet />
        </Layout>
    )
}

export const getNavigationMenuItems = (t: TFunction, tTerminology: TFunction): NavigationMenuItem[] => [
    {
        label: t('dashboard'),
        Icon: <SquaresFourIcon size={tokens.SizingIconM} />,
        path: STATIC_APP_PATHS.dashboard,
    },
    {
        label: tTerminology('assets'),
        Icon: <BuildingOfficeIcon size={tokens.SizingIconM} />,
        path: STATIC_APP_PATHS.assetListPage,
    },
    {
        label: tTerminology('orders'),
        Icon: <FilesIcon size={tokens.SizingIconM} />,
        path: STATIC_APP_PATHS.orderListPage,
        pathPatterns: [
            STATIC_APP_PATHS.orderListPage,
            STATIC_APP_PATHS.createOrderPage,
            DYNAMIC_APP_PATHS.orderDetailsPage,
            DYNAMIC_APP_PATHS.orderTaskListPage,
            DYNAMIC_APP_PATHS.orderThreadListPage,
            DYNAMIC_APP_PATHS.createOrderPage,
        ],
    },
    {
        label: tTerminology('tasks'),
        Icon: <ClipboardTextIcon size={tokens.SizingIconM} />,
        path: STATIC_APP_PATHS.taskOverviewPage,
    },
    {
        label: tTerminology('analysis'),
        Icon: <PulseIcon size={tokens.SizingIconM} />,
        path: STATIC_APP_PATHS.analysisPage,
    },
]

export const getInboxNavigationItem = (t: TFunction): NavigationMenuItem => ({
    label: t('inbox'),
    Icon: <TrayIcon size={tokens.SizingIconM} />,
    path: STATIC_APP_PATHS.threadOverviewPage,
})

export const getUserNavigationItems = (user: User | undefined, t: TFunction, path = '#'): NavigationMenuItemWithImage => ({
    label: user?.first_name || t('profile'),
    Icon: <UserCircleIcon size={tokens.SizingIconM} />,
    path,
    imageUrl: user?.profile_image_url,
    imageThumbnailUrl: user?.profile_thumbnail_image_url,
    isPlatformUser: user?.is_platform_user,
    overlayIcon: <GearIcon size={tokens.SizingIconS} weight="bold" />,
})
